import React, { useCallback, useEffect, useState } from 'react';
import { Wallet, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import BuyATBalanceModal from './BuyATBalanceModal';

/**
 * ATWalletBadge — common top-right widget for every page where AT is spent.
 * Shows live AT balance (+ commission balance optionally) with a "Buy AT" button
 * that opens the standard BuyATBalanceModal. Clicking the balance itself jumps
 * to the AVX Wallet page P2P tab so user can Send / Receive / Scan & Pay.
 */
const ATWalletBadge = ({ showCommission = false, className = '', compact = false }) => {
  const [wallet, setWallet] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get('/avx/wallet/me');
      setWallet(r.data?.wallet || {});
    } catch { /* not logged in / ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className={`flex items-center gap-1.5 ${className}`} data-testid="at-wallet-badge">
        <button
          onClick={() => navigate('/avx?tab=p2p')}
          title="Send / Receive / Scan & Pay"
          data-testid="at-wallet-balance-btn"
          className={`flex items-center gap-1 rounded-xl border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 transition ${compact ? 'px-2 py-1' : 'px-3 py-1.5'}`}
        >
          <Wallet className={`text-amber-300 ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
          <div className="leading-tight text-left">
            <div className={`font-extrabold text-amber-200 ${compact ? 'text-xs' : 'text-sm'}`} data-testid="at-wallet-balance">
              {wallet ? (wallet.at_balance || 0).toFixed(compact ? 0 : 2) : '…'} <span className="text-[9px] font-normal text-amber-300/80">AT</span>
            </div>
            {showCommission && wallet && !compact && (
              <div className="text-[10px] text-emerald-300" data-testid="at-commission-balance">
                Comm: {(wallet.commission_balance || 0).toFixed(2)} AT
              </div>
            )}
          </div>
        </button>
        <button
          onClick={() => setShowBuy(true)}
          data-testid="buy-at-btn"
          title="Buy AT"
          className={`flex items-center gap-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 transition ${compact ? 'px-1.5 py-1 text-[10px]' : 'px-3 py-2 text-xs'}`}
        >
          <Plus className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {compact ? '' : 'Buy AT'}
        </button>
      </div>
      {showBuy && (
        <BuyATBalanceModal open={showBuy} onClose={() => { setShowBuy(false); load(); }} />
      )}
    </>
  );
};

export default ATWalletBadge;
